/* @ngInject */

export default (
  AssetFinanceProposalAmortizationSchedule,
  AssetFinanceProposalAssociatedFee,
  PaymentFrequency
) => {
  /*
   * Constructor
   * --------------------------------------------------------------------------------
   */
  function AssetFinanceProposal(
    totalAssetCost,
    totalAssetNetCost,
    grossCost,
    amountExGstVat,
    netCost,
    gstVatAmount,
    loanAmount,
    netAmountFinanced,
    isNetAmountFinancedEdited,
    netAmountFinancedEdited,
    term,
    interestRate,
    isInterestRateEdited,
    brokerCommissionValue,
    brokerCommissionPercent,
    rateDiscount,
    isRateDiscountEdited,
    productType,
    balloonValue,
    balloonPercent,
    brokerOriginationFee,
    isBrokerOriginationFeeCapitalized,
    processingFee,
    brokerFee,
    lvr,
    estimatedBorrowerRate,
    estimatedRepayment,
    estimatedRepaymentBuffer,
    gstOfEstimatedRepayment,
    estimatedRepaymentWithGst,
    netMonthlySurplus,
    loanType,
    totalBrokerBrokerage,
    totalAggregatorBrokerage,
    totalBrokerage,
    aggregatorCommissionValue,
    payments,
    paymentFrequency,
    isStructured,
    amortizationSchedules,
    associatedFees,
    isAssociatedFeesEdited,
    isEdit
  ) {
    this.totalAssetCost = totalAssetCost;
    this.totalAssetNetCost = totalAssetNetCost;
    this.grossCost = grossCost;
    this.amountExGstVat = amountExGstVat;
    this.netCost = netCost;
    this.gstVatAmount = gstVatAmount;
    this.loanAmount = loanAmount;
    this.netAmountFinanced = netAmountFinanced;
    this.isNetAmountFinancedEdited = isNetAmountFinancedEdited;
    this.netAmountFinancedEdited = netAmountFinancedEdited;
    this.term = term;
    this.interestRate = interestRate;
    this.isInterestRateEdited = isInterestRateEdited;
    this.brokerCommissionValue = brokerCommissionValue;
    this.brokerCommissionPercent = brokerCommissionPercent;
    this.rateDiscount = rateDiscount;
    this.isRateDiscountEdited = isRateDiscountEdited;
    this.productType = productType;
    this.balloonValue = balloonValue;
    this.balloonPercent = balloonPercent;
    this.brokerOriginationFee = brokerOriginationFee;
    this.isBrokerOriginationFeeCapitalized = isBrokerOriginationFeeCapitalized;
    this.processingFee = processingFee;
    this.brokerFee = brokerFee;
    this.lvr = lvr;
    this.estimatedBorrowerRate = estimatedBorrowerRate;
    this.estimatedRepayment = estimatedRepayment;
    this.estimatedRepaymentBuffer = estimatedRepaymentBuffer;
    this.gstOfEstimatedRepayment = gstOfEstimatedRepayment;
    this.estimatedRepaymentWithGst = estimatedRepaymentWithGst;
    this.netMonthlySurplus = netMonthlySurplus;
    this.loanType = loanType;
    this.totalBrokerBrokerage = totalBrokerBrokerage;
    this.totalAggregatorBrokerage = totalAggregatorBrokerage;
    this.totalBrokerage = totalBrokerage;
    this.aggregatorCommissionValue = aggregatorCommissionValue;
    this.payments = payments;
    this.paymentFrequency = paymentFrequency;
    this.isStructured = isStructured;
    this.amortizationSchedules = amortizationSchedules;
    this.associatedFees = associatedFees;
    this.isAssociatedFeesEdited = isAssociatedFeesEdited;
    this.isEdit = isEdit;
  }

  /*
   * Static Methods
   * --------------------------------------------------------------------------------
   */
  AssetFinanceProposal.Blank = function () {
    return AssetFinanceProposal.Build({
      totalAssetCost: 0,
      totalAssetNetCost: 0,
      grossCost: 0,
      amountExGstVat: 0,
      netCost: 0,
      gstVatAmount: 0,
      loanAmount: 0,
      netAmountFinanced: 0,
      isNetAmountFinancedEdited: false,
      netAmountFinancedEdited: 0,
      term: 0,
      interestRate: 0,
      isInterestRateEdited: false,
      brokerCommissionValue: 0,
      brokerCommissionPercent: 0,
      rateDiscount: 0,
      isRateDiscountEdited: false,
      productType: 0,
      balloonValue: 0,
      balloonPercent: 0,
      brokerOriginationFee: 0,
      isBrokerOriginationFeeCapitalized: true,
      processingFee: 0,
      brokerFee: 0,
      lvr: 0,
      estimatedBorrowerRate: 0,
      estimatedRepayment: 0,
      estimatedRepaymentBuffer: 0,
      gstOfEstimatedRepayment: 0,
      estimatedRepaymentWithGst: 0,
      netMonthlySurplus: 0,
      loanType: '',
      totalBrokerBrokerage: 0,
      totalAggregatorBrokerage: 0,
      totalBrokerage: 0,
      aggregatorCommissionValue: 0,
      payments: false,
      paymentFrequency: PaymentFrequency.monthly,
      isStructured: false,
      amortizationSchedules: [],
      associatedFees: [],
      isAssociatedFeesEdited: false,
      isEdit: false
    });
  };

  AssetFinanceProposal.Build = function (data) {
    return new AssetFinanceProposal(
      data.totalAssetCost || 0,
      data.totalAssetNetCost || 0,
      data.grossCost || 0,
      data.amountExGstVat || 0,
      data.netCost || 0,
      data.gstVatAmount || 0,
      data.loanAmount || 0,
      data.netAmountFinanced || 0,
      data.isNetAmountFinancedEdited || false,
      data.netAmountFinancedEdited || 0,
      data.term || 0,
      data.interestRate || 0,
      data.isInterestRateEdited || false,
      data.brokerCommissionValue || 0,
      data.brokerCommissionPercent || 0,
      data.rateDiscount || 0,
      data.isRateDiscountEdited || false,
      data.productType || 0,
      data.balloonValue || 0,
      data.balloonPercent || 0,
      data.brokerOriginationFee || 0,
      data.isBrokerOriginationFeeCapitalized || false,
      data.processingFee || 0,
      data.brokerFee || 0,
      data.lvr || 0,
      data.estimatedBorrowerRate || 0,
      data.estimatedRepayment || 0,
      data.estimatedRepaymentBuffer || 0,
      data.gstOfEstimatedRepayment || 0,
      data.estimatedRepaymentWithGst || 0,
      data.netMonthlySurplus || 0,
      data.loanType || '',
      data.totalBrokerBrokerage || 0,
      data.totalAggregatorBrokerage || 0,
      data.totalBrokerage || 0,
      data.aggregatorCommissionValue || 0,
      data.payments || false,
      data?.paymentFrequency
        ? PaymentFrequency.of(data?.paymentFrequency?.name || `f${data?.paymentFrequency}`)
        : PaymentFrequency.monthly,
      data.isStructured || false,
      _.map(_.compact(data.amortizationSchedules), (amortizationSchedule) =>
        AssetFinanceProposalAmortizationSchedule.Build(amortizationSchedule)
      ),
      _.map(_.compact(data.associatedFees), (associatedFee) =>
        AssetFinanceProposalAssociatedFee.Build(associatedFee)
      ),
      data.isAssociatedFeesEdited || false,
      data.isEdit || false
    );
  };

  return AssetFinanceProposal;
};
